import { useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import {
    Box,
    FormControlLabel,
    FormGroup,
    Switch,
    Typography,
} from '@mui/material';
import { useLocalStorage } from '../hooks/useLocalStorage';

import {
    LOG_LOGGER_KEY,
    LOG_UI_TRACKING_EVENTS_KEY,
    NOISE_CANCELLATION_SLOT_ELEMENT_ID,
} from './constants';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { envConfig } from '../constants/envConfig';

const DebugModeFloatingButton = () => {
    const active = Boolean(
        envConfig.MODE === 'development' ||
            localStorage.getItem('debug/enableDebugMode'),
    );
    const wrapperElement = useRef<HTMLDivElement>(null);
    const [hovered, setHovered] = useState(false);
    const [logUiTrackingEvents, setLogUiTrackingEvents] = useLocalStorage(
        LOG_UI_TRACKING_EVENTS_KEY,
        active,
    );
    const [logLogger, setLogLogger] = useLocalStorage(LOG_LOGGER_KEY, false);
    const [reactQueryDevToolsVisible, showReactQueryDevTools] = useState(false);

    // have to use native DOM events because React event system doesn't work well with portals
    useEffectOnce(() => {
        if (!wrapperElement.current) {
            return;
        }

        const handleMouseMove = () => setHovered(true);
        const handleMouseLeave = () => setHovered(false);

        wrapperElement.current.addEventListener('mousemove', handleMouseMove);
        wrapperElement.current.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            wrapperElement.current?.removeEventListener(
                'mousemove',
                handleMouseMove,
            );
            wrapperElement.current?.removeEventListener(
                'mouseleave',
                handleMouseLeave,
            );
        };
    });

    if (active) {
        return (
            <>
                {reactQueryDevToolsVisible && (
                    <ReactQueryDevtoolsPanel
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            height: 250,
                        }}
                        onClose={() => showReactQueryDevTools(true)}
                    />
                )}
                <Box
                    ref={wrapperElement}
                    sx={{
                        position: 'fixed',
                        display: 'flex',
                        flexDirection: 'column-reverse',
                        gap: 1,
                        bottom: 0,
                        left: 0,
                        zIndex: 1000,
                        backgroundColor: 'rgb(225,225,225, 0.3)',
                        m: 1,
                        p: 1,
                        borderRadius: 1,

                        '&:hover': {
                            backgroundColor: 'rgb(225,225,225, 0.75)',
                        },
                    }}
                >
                    <Typography variant="caption" color="secondary">
                        Debug mode
                    </Typography>
                    <Box sx={{ display: hovered ? 'block' : 'none' }}>
                        <FormGroup sx={{ px: '12px' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={reactQueryDevToolsVisible}
                                        onChange={(e) =>
                                            showReactQueryDevTools(
                                                e.target.checked,
                                            )
                                        }
                                    />
                                }
                                label="ReactQuery DevTools"
                                labelPlacement="end"
                            />
                        </FormGroup>

                        <FormGroup sx={{ px: '12px' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={logUiTrackingEvents}
                                        onChange={(e) =>
                                            setLogUiTrackingEvents(
                                                e.target.checked,
                                            )
                                        }
                                    />
                                }
                                label="Log UI tracking events to console"
                                labelPlacement="end"
                            />
                        </FormGroup>
                        <FormGroup sx={{ px: '12px' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={logLogger}
                                        onChange={(e) =>
                                            setLogLogger(e.target.checked)
                                        }
                                    />
                                }
                                label="Log all logger messages"
                                labelPlacement="end"
                            />
                        </FormGroup>
                        <FormGroup sx={{ px: '12px' }}>
                            <div id={NOISE_CANCELLATION_SLOT_ELEMENT_ID} />
                        </FormGroup>
                    </Box>
                </Box>
            </>
        );
    } else {
        return null;
    }
};

export default DebugModeFloatingButton;
